import Table from "@shared/components/Table";
import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import SigneesForm from "./SigneesForm";
import SigneesTranslations from "./SigneesTranslations";
import { signeesService } from "../services/signees";
import { notifySuccess, notifyError } from "@config/tostifyConfig/tostifyConfig";
import DeleteModal from "@shared/components/DeleteModal";
import { COMMON_BUTTONS } from "@config/actionButtonConfigs";
import ActionButtons from "@shared/components/ActionButtons";
import { useStatusBadge } from "@shared/components/StatusBadge";
import {
    extractErrorMessage,
    shouldBlockFetchError,
    showBlockingError,
} from '@shared/services/blockingError';

const SIGNEES_FETCH_OPTIONS = { blockingError: true };

function handleSigneesAccessError(error) {
    if (shouldBlockFetchError(error, SIGNEES_FETCH_OPTIONS)) {
        showBlockingError(extractErrorMessage(error));
        return true;
    }

    return false;
}

export default function Signees() {
    const { t } = useTranslation();
    const [selectedActive, setSelectedActive] = useState('true'); // Default to active
    const [selectedData, setSelectedData] = useState(null);
    const [modalState, setModalState] = useState({ visible: false, type: null, editData: null });
    const [signees, setSignees] = useState([]);
    const [searchQuery, setSearchQuery] = useState({ personName: '', personFatherName: '', personSurName: '' });

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const { statusBadge } = useStatusBadge();

    const handleOpenModal = (type) => {
        let editData = null;
        if (type === 'edit' && selectedData) {
            const selectedSignee = signees.find(signee => signee.id === selectedData);
            editData = selectedSignee;
        }
        setModalState({ visible: true, type: type, editData: editData });
    }

    const fetchSignees = async () => {
        try {
            setLoading(true);
            setError(null);

            const searchParams = {};
            if (searchQuery.personName) {
                searchParams.personName = searchQuery.personName;
            }
            if (searchQuery.personFatherName) {
                searchParams.personFatherName = searchQuery.personFatherName;
            }
            if (searchQuery.personSurName) {
                searchParams.personSurName = searchQuery.personSurName;
            }
            if (selectedActive !== null && selectedActive !== '') {
                searchParams.active = selectedActive === 'true';
            }

            const paginationParams = {
                pageNumber: pageNumber,
                pageSize: pageSize
            };

            const signeesData = await signeesService.getAllSignees(searchParams, paginationParams);

            if (Array.isArray(signeesData)) {
                setSignees(signeesData);
                setTotalElements(signeesData.length);
                setTotalPages(1);
            } else if (signeesData?.content) {
                setSignees(signeesData.content);
                setTotalElements(signeesData.totalElements || signeesData.content.length);
                setTotalPages(signeesData.totalPages || 1);
            } else {
                setSignees([]);
                setTotalElements(0);
                setTotalPages(0);
            }

        } catch (error) {
            console.error('❌ [fetchSignees] Error fetching signees:', error);
            setSignees([]);
            setTotalElements(0);
            setTotalPages(0);
            if (!handleSigneesAccessError(error)) {
                setError('Failed to fetch signees');
                notifyError(t('common.errorMessage'));
            } else {
                setError(null);
            }
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchSignees();
    }, [pageNumber, pageSize, selectedActive]);

    const handleSearchClick = async (getDataFunc, params = {}) => {
        setPageNumber(0);
        fetchSignees();
    };

    const handleCloseModal = () => {
        setModalState({ visible: false, type: null, editData: null });
    }

    const handleConfirm = async (formData) => {
        try {
            if (modalState.type === 'add') {
                await signeesService.addSignee(formData);
                notifySuccess(t('common.createdSuccessfully'));
            } else if (modalState.type === 'edit') {
                await signeesService.updateSignee(selectedData, formData);
                notifySuccess(t('common.updatedSuccessfully'));
            } else if (modalState.type === 'delete') {
                await signeesService.deleteSignee(selectedData);
                notifySuccess(t('common.deletedSuccessfully'));
                setSelectedData(null);
            }

            handleCloseModal();
            await fetchSignees();

        } catch (error) {
            console.error('Error in handleConfirm:', error);
            if (!handleSigneesAccessError(error)) {
                notifyError(t('common.error'));
            }
        }
    }

    const actionButtons = (
        <ActionButtons
            buttons={[
                { ...COMMON_BUTTONS.ADD, onClick: () => handleOpenModal('add') },
                { ...COMMON_BUTTONS.EDIT, onClick: () => handleOpenModal('edit') },
                { ...COMMON_BUTTONS.DELETE, onClick: () => handleOpenModal('delete') },
                { ...COMMON_BUTTONS.TRANSLATE, onClick: () => handleOpenModal('translations') },
            ]}
            selectedRecord={selectedData}
        />
    );
    const renderYesNoBadge = (value) => {
        const isTruthy = value === true || value === 'true' || value === 1 || value === '1';

        return (
            <span className={`status-badge ${isTruthy ? 'active' : 'inactive'}`}>
                {t(`common.${isTruthy ? 'yes' : 'no'}`)}
            </span>
        );
    };

    const columns = [
        {
            key: "employeeName",
            label: t('organizationalStructure.signees.table.headers.employeeName'),
            render: (item) => `${item.personName || ''} ${item.personFatherName || ''} ${item.personSurName || ''}`.trim()
        },
        {
            key: "empBranchId",
            label: t('organizationalStructure.signees.table.headers.companies'),
        },
        {
            key: "jobDescription",
            label: t('organizationalStructure.signees.table.headers.positions'),
        },
        {
            key: "docSign1",
            label: t('organizationalStructure.signees.table.headers.firstSignatures'),
            renderStatusBadge: true
        },
        {
            key: "docSign2",
            label: t('organizationalStructure.signees.table.headers.secondSignatures'),
            renderStatusBadge: true
        },
        {
            key: "active",
            label: t('organizationalStructure.signees.table.headers.active'),
            renderStatusBadge: true
        },
        {
            key: "translated",
            label: t('organizationalStructure.signees.table.headers.translations'),
            renderStatusBadge: true
        }
    ];
    const searchOptions = [
        {
            key: "personName",
            label: "firstName",
        },
        {
            key: "personFatherName",
            label: "fathersName",
        },
        {
            key: "personSurName",
            label: "surname",
        }
    ];

    const handleCheckboxChange = (index) => {
        const signee = signees[index];
        setSelectedData(selectedData === signee.id ? null : signee.id);
    }


    return (
       <>
        <Table 
        title={t('organizationalStructure.signees.title')}
        columns={columns}
        data={(signees || []).map((item, index) => ({ ...item, index }))}
        showCustomSearch={false}
        searchOptions={searchOptions}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        handleSearchClick={handleSearchClick}
        getData={fetchSignees}
        handleCheckboxChange={handleCheckboxChange}
        selectedData={selectedData}
        showActiveSelect={true}
        showPageSizeSelector={false}
        selectedActive={selectedActive}
        setSelectedActive={setSelectedActive}
        actionButtons={actionButtons}
        showPagination={true}
        pageNumber={pageNumber}
        pageSize={pageSize}
        setPageNumber={setPageNumber}
        setPageSize={setPageSize}
        totalElements={totalElements}
        totalPages={totalPages}
        renderCell={(item, column) => {
            if (column.key === 'docSign1' || column.key === 'docSign2') {
                return renderYesNoBadge(item[column.key]);
            }

            return statusBadge(item, column);
        }}
        />
        {modalState.visible && modalState.type === 'add' && (
            <SigneesForm
                onClose={() => setModalState({ visible: false, type: null, editData: null })}
                editData={modalState.editData}
                handleConfirm={handleConfirm}
            />
        )}
        {modalState.visible && modalState.type === 'edit' && (
            <SigneesForm
                onClose={() => setModalState({ visible: false, type: null, editData: null })}
                editData={modalState.editData}
                handleConfirm={handleConfirm}
            />
        )}
        {modalState.visible && modalState.type === 'translations' && (
            <SigneesTranslations
                onClose={() => setModalState({ visible: false, type: null, editData: null })}
                signee={signees.find(s => s.id === selectedData)}
                onSuccess={() => {
                    fetchSignees();
                }}
            />
        )}
        {modalState.visible && modalState.type === 'delete' && (() => {
            const selectedSignee = signees.find(s => s.id === selectedData);
            return (
                <DeleteModal
                    onClose={() => setModalState({ visible: false, type: null, editData: null })}
                    onConfirm={handleConfirm}
                    selectedData={selectedData}
                    name={`${selectedSignee?.personName || ''} ${selectedSignee?.personFatherName || ''} ${selectedSignee?.personSurName || ''}`.trim()}
                    entityType="record"
                    handleConfirm={handleConfirm}
                />
            );
        })()}
    </>

    )
}